import axios, { HttpStatusCode } from "axios";
import store from "../app/store";
import { setUnauthorizedForceLogout } from "../rtk-query/features/stripe/stripeSlice";
import { Observable } from "rxjs";
import { attachApiTracker, createApiTracker } from "../analytics/utils/apiTracker";

const BASE_URL =
  process.env.REACT_APP_CHATBOT_API_URL || "https://api.example.com";

const chatbotApiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

chatbotApiClient.interceptors.request.use(function (config) {
  const token = store.getState()?.authReducer?.userData?.accessToken;
  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

attachApiTracker(chatbotApiClient);

const normalizeResponse = (response) => ({
  ...response,
  statusCode: response?.status,
  data: {
    ...response?.data,
    statusCode: response?.status,
  },
});

const normalizeError = (error) => {
  const response = error?.response;
  if (response?.data?.error) {
    return { ...response.data, statusCode: response.status };
  }

  return {
    data: response?.data,
    error: error?.code,
    message: error?.message,
    status: response?.statusText,
    statusCode: response?.status,
  };
};

const _get = async (url, config = {}) =>
  chatbotApiClient.get(url, config).then(normalizeResponse).catch(normalizeError);

const _post = async (url, data = {}, config = {}) => {
  return chatbotApiClient
    .post(url, data, config)
    .then(normalizeResponse)
    .catch((error) => {
      if (error?.response?.status === HttpStatusCode.Unauthorized) {
        store.dispatch(setUnauthorizedForceLogout(true));
      }
      return normalizeError(error);
    });
};

const _put = async (url, data = {}, config = {}) =>
  chatbotApiClient.put(url, data, config).then(normalizeResponse).catch(normalizeError);

const _delete = async (url, config = {}) =>
  chatbotApiClient.delete(url, config).then(normalizeResponse).catch(normalizeError);

const streamChat = (url, payload) =>
  new Observable((observer) => {
    const token = store.getState()?.authReducer?.userData?.accessToken;

    const controller = new AbortController();
    const tracker = createApiTracker();
    const requestConfig = {
      method: "POST",
      metadata: tracker.onRequest({
        metadata: { apiName: "streamChat" },
      }).metadata,
    };
    let responseTracked = false;

    fetch(`${BASE_URL}/${url}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    })
      .then(async (response) => {
        responseTracked = true;
        tracker.onResponse({
          config: requestConfig,
          status: response.status,
          ok: response.ok,
          data: !response.ok ? { message: response.statusText } : undefined,
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error("ReadableStream not supported");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        const emitDataLine = (line) => {
          if (line) {
            observer.next({ data: line });
          }
        };

        try {
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              buffer += decoder.decode();

              if (buffer) {
                emitDataLine(buffer);
              }

              observer.complete();
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            lines.forEach(emitDataLine);

            if (buffer && !buffer.startsWith("data:")) {
              emitDataLine(buffer);
              buffer = "";
            }
          }
        } catch (err) {
          observer.error(err);
        } finally {
          reader.releaseLock();
        }
      })
      .catch((err) => {
        if (!responseTracked) {
          tracker.onResponse({ config: requestConfig, ok: false });
        }
        if (err.name !== "AbortError") {
          observer.error(err);
        }
      });

    return () => {
      controller.abort();
    };
  });

export { _get, _post, _put, _delete, streamChat };
